import { VehicleMediaQueryParams } from '@euler/app/flows/record/functions/useServiceRecordMedias';
import { MediaWithSource } from '@euler/model';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import { ReactNode, createContext, memo, useCallback } from 'react';
import { BehaviorSubject } from 'rxjs';
import { MediasPageContextProps } from './types';

export const MediasPageContext = createContext<MediasPageContextProps>(
  null as any,
);

export const MediasPageProvider = memo(
  ({
    queryParams,
    data,
    children,
  }: {
    queryParams: VehicleMediaQueryParams;
    data: MediaWithSource[];
    children: ReactNode;
  }) => {
    const isSelectionModeActive$ = useCreation(
      () => new BehaviorSubject<boolean>(false),
      [],
    );

    const selection$ = useCreation(
      () => new BehaviorSubject<MediaWithSource[]>([]),
      [],
    );

    const [selection, updateSelection] = useBehaviorSubject(selection$);

    const selectAll = useCallback(() => {
      updateSelection(data);
    }, [data, updateSelection]);

    const deselectAll = useCallback(() => {
      updateSelection([]);
    }, [updateSelection]);

    const select = useCallback(
      (media: MediaWithSource) => {
        updateSelection(list =>
          list.some(x => x._id === media._id) ? list : [...list, media],
        );
      },
      [updateSelection],
    );

    const deselect = useCallback(
      (media: MediaWithSource) => {
        updateSelection(list => list.filter(x => x._id !== media._id));
      },
      [updateSelection],
    );

    const isAllSelected =
      selection.length > 0 && selection.length === data.length;

    return (
      <MediasPageContext.Provider
        value={{
          data,
          isSelectionModeActive$,
          isAllSelected,
          selection$,
          selection,
          queryParams,
          selectAll,
          deselectAll,
          select,
          deselect,
        }}
      >
        {children}
      </MediasPageContext.Provider>
    );
  },
);
